/* eslint-disable id-length */
import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { styled } from 'styled-components';
import {
  SV_AREA_CANVAS_WIDTH_PX,
  SV_AREA_CANVAS_HEIGHT_PX,
  TWO_FIFTY_FIVE,
  DEFAULT_MIN_CONTRAST,
} from './consts';
import { drawSmoothCurve } from './canvas-utils';
import { getAccessibleColorByValueChannelChange } from './contrast-utils';
import { convertToRgb } from './color-conversion-utils';

const Canvas = styled.canvas`
  display: block;
  width: 100%;
`;

type HSVSaturationValueCanvasProps = {
  hue: number;
  shouldShowContrastCurve: boolean;
  minimumContrastRatio: number;
  colorForComparison: string;
  opacityForContrastCalculation: number;
};

const calculateContrastCurve = (
  width: number,
  height: number,
  hue: number,
  minimumContrastRatio: HSVSaturationValueCanvasProps['minimumContrastRatio'],
  colorForComparison: HSVSaturationValueCanvasProps['colorForComparison'],
  limit: 'lower' | 'upper',
  opacityForContrastCalculation: HSVSaturationValueCanvasProps['opacityForContrastCalculation'],
): { x: number; y: number }[] => {
  const contrastPoints: { x: number; y: number }[] = [];

  for (let x = 0; x < width; x += 1) {
    const saturationChanel = x / (width - 1);

    // Search direction: upper searches from bottom to top, lower from top to bottom
    const initialValueChannelForSearch = limit === 'upper' ? 0 : 1;
    const colorForXAsHsv = getAccessibleColorByValueChannelChange({
      originalColor: { h: hue, s: saturationChanel, v: initialValueChannelForSearch, mode: 'hsv' },
      colorForComparison,
      minContrast: minimumContrastRatio,
      direction: limit === 'upper' ? 'brighter' : 'darker',
      opacityForContrastCalculation,
      // We set shouldConvertToHex to false to make the curve smoother.
      shouldConvertToHex: false,
    });

    if (colorForXAsHsv != null) {
      const point = {
        x: saturationChanel * width,
        y: height - colorForXAsHsv.v * height, // Convert value
      };

      contrastPoints.push(point);
    }

    if (colorForXAsHsv == null) {
      // If we hit a point where no color is accessible, we can bail out. the curve only goes up and we must have hit the top.
      break;
    }

    // if we've hit the top, we can bail. these curves only go up.
    if (colorForXAsHsv.v >= 1) {
      break;
    }
  }

  return contrastPoints;
};

const drawGradientBackground = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  hue: number,
): void => {
  const img = ctx.createImageData(width, height);
  const { data } = img;

  for (let y = 0; y < height; y++) {
    const v = 1 - y / (height - 1);
    for (let x = 0; x < width; x++) {
      const s = x / (width - 1);
      const rgb = convertToRgb({ h: hue, s, v, mode: 'hsv' });
      const idx = 4 * (y * width + x);
      data[idx] = rgb.r * TWO_FIFTY_FIVE;
      data[idx + 1] = rgb.g * TWO_FIFTY_FIVE;
      data[idx + 2] = rgb.b * TWO_FIFTY_FIVE;
      data[idx + 3] = TWO_FIFTY_FIVE;
    }
  }
  ctx.putImageData(img, 0, 0);
};

/**
 * Canvas component that draws a 2D saturation/value gradient.
 *
 * Features:
 * - X-axis: saturation (0-1, left to right)
 * - Y-axis: value (1-0, top to bottom)
 * - Optional contrast threshold curve overlay
 */
export const HSVSaturationValueCanvas = ({
  hue,
  shouldShowContrastCurve = false,
  minimumContrastRatio = DEFAULT_MIN_CONTRAST,
  colorForComparison,
  opacityForContrastCalculation,
}: HSVSaturationValueCanvasProps): JSX.Element => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const { width, height } = canvas;

    drawGradientBackground(ctx, width, height, hue);

    if (shouldShowContrastCurve && colorForComparison) {
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = 'white';

      // curve below which contrast is sufficient
      const maxHsvValueChannelCurve = calculateContrastCurve(
        width,
        height,
        hue,
        minimumContrastRatio,
        colorForComparison,
        'upper',
        opacityForContrastCalculation,
      );

      // curve above which contrast is sufficient
      const minHsvValueChannelCurve = calculateContrastCurve(
        width,
        height,
        hue,
        minimumContrastRatio,
        colorForComparison,
        'lower',
        opacityForContrastCalculation,
      );

      if (minHsvValueChannelCurve.length > 0) {
        drawSmoothCurve(ctx, minHsvValueChannelCurve);
      }
      if (maxHsvValueChannelCurve.length > 0) {
        drawSmoothCurve(ctx, maxHsvValueChannelCurve);
      }
    }
  }, [
    hue,
    shouldShowContrastCurve,
    minimumContrastRatio,
    colorForComparison,
    opacityForContrastCalculation,
  ]);

  return (
    <Canvas
      ref={canvasRef}
      height={SV_AREA_CANVAS_HEIGHT_PX}
      width={SV_AREA_CANVAS_WIDTH_PX}
    />
  );
};
